import { KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

type Options = {
    itemCount: number;
    shouldAutoActivate: boolean;
    onEnterSelect: (index: number) => void;
};

/**
 * Manages keyboard navigation (ArrowUp / ArrowDown / Enter) for the search overlay listbox,
 * following the WAI-ARIA APG combobox pattern.
 *
 * Exposes the currently active option index for `aria-activedescendant`, a ref callback to
 * collect item nodes (so the active one can be scrolled into view), and a keydown handler
 * to attach on the input. When `shouldAutoActivate` is true and items exist, the first row
 * is pre-selected so Enter submits without requiring ArrowDown first.
 */
export function useSearchKeyboard({ itemCount, shouldAutoActivate, onEnterSelect }: Options) {
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

    useEffect(() => {
        setActiveIndex(shouldAutoActivate && itemCount > 0 ? 0 : null);
    }, [shouldAutoActivate, itemCount]);

    const itemRef = useCallback((index: number, el: HTMLAnchorElement | null) => {
        itemRefs.current[index] = el;
    }, []);

    const onKeyDown = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                if (itemCount === 0) return;
                setActiveIndex((prev) => {
                    const next = prev === null ? 0 : Math.min(prev + 1, itemCount - 1);
                    itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
                    return next;
                });
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                if (itemCount === 0) return;
                setActiveIndex((prev) => {
                    if (prev === null) return null;
                    const next = Math.max(prev - 1, 0);
                    itemRefs.current[next]?.scrollIntoView({ block: 'nearest' });
                    return next;
                });
            } else if (event.key === 'Enter') {
                if (activeIndex === null) return;
                event.preventDefault();
                onEnterSelect(activeIndex);
            }
        },
        [activeIndex, itemCount, onEnterSelect],
    );

    return { activeIndex, setActiveIndex, itemRef, onKeyDown };
}
